import { useState, useEffect } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Wordmark } from '@/components/wordmark'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { OAuthButtons, OAuthDivider } from '@/components/oauth-buttons'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { SiteFooter } from '@/components/site-footer'
import { ArrowRight } from 'lucide-react'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import { CheckEmailStep } from '@/components/check-email-step'
import { useFreePlan } from '@/hooks/use-free-plan'
import { authRedirectTarget, linkRedirect, rememberRedirect } from '@/lib/post-auth-redirect'
import { PRO_ADDS } from '@/lib/free-plan'
import { FREE_DOMAIN_RULE, PAID_ANY_EMAIL, looksLikeFreeMail } from '@/lib/free-domain'
import { isBillingInterval, isSignupPlan, tiers, type BillingInterval, type SignupPlan } from '@/lib/plans'

export const Route = createFileRoute('/signup')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string; plan?: SignupPlan; interval?: BillingInterval } => ({
    redirect: (search.redirect as string) || undefined,
    plan: isSignupPlan(search.plan) ? search.plan : undefined,
    interval: isBillingInterval(search.interval) ? search.interval : undefined,
  }),
  component: SignupPage,
  head: () => ({
    meta: [
      { title: 'Sign Up — Start Free Uptime Monitoring | Uptrack' },
      { name: 'description', content: 'Create an Uptrack account. Start free with 5 monitors at 30-second checks, or choose a subscription for more monitors and locations.' },
      { property: 'og:title', content: 'Sign Up for Uptrack — Uptime Monitoring' },
      { property: 'og:description', content: 'Start free with 5 monitors checked every 30 seconds, or choose Pro, Team, or Business.' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/signup' },
    ],
  }),
})

function SignupPage() {
  const { redirect, plan, interval } = Route.useSearch()
  return <SignupView redirectTo={redirect} plan={plan} interval={interval} />
}

/** The free plan cannot take this sign-up: it is full (cap reached) or closed (switched off). */
function FreePlanUnavailable({ full }: { full: boolean }) {
  return (
    <div className="flex min-h-screen flex-col">
      <main id="main" className="flex flex-1 items-start justify-center px-4 py-20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" aria-label="Uptrack home" className="mx-auto mb-2 inline-flex"><Wordmark height={28} /></Link>
            <h1 data-slot="card-title" className="text-2xl leading-none font-semibold">
              {full ? 'The free plan is full right now' : 'The free plan is closed to new accounts'}
            </h1>
            <CardDescription>
              {full
                ? 'We limit the number of free accounts, and the limit is reached. New accounts cannot join the free plan at the moment.'
                : 'We are not taking new free accounts at the moment.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border bg-muted p-4 text-left">
              <p className="font-semibold text-foreground">Pro: $3/mo, or $30 a year</p>
              <p className="mt-1 text-sm text-body">
                Every monitor checks every 30 seconds. {PRO_ADDS[0].toUpperCase()}{PRO_ADDS.slice(1)}. 30-day money-back guarantee.
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/signup" search={{ plan: 'pro', interval: 'annual' }}>
                Choose Pro
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/pricing">See all plans</Link>
            </Button>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}

export function SignupView({
  redirectTo,
  plan,
  interval,
}: {
  redirectTo?: string
  plan?: SignupPlan
  interval?: BillingInterval
}) {
  const freePlan = useFreePlan()
  // `?plan=free` while the plan is full or closed: show the Pro offer, not the form.
  const freeUnavailable = plan === 'free' && !freePlan.isOpen
  const target = authRedirectTarget({ redirect: redirectTo, plan: freeUnavailable ? undefined : plan, interval })
  const planName = plan === 'free' ? 'Free' : tiers.find((t) => t.plan === plan)?.name
  const freeOpen = freePlan.isOpen
  const [email, setEmail] = useState('')

  // Store redirect URL for post-auth navigation (survives OAuth redirects)
  useEffect(() => {
    rememberRedirect(target)
  }, [target])
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const cooldown = useResendCooldown()

  async function handleSendMagicLink(e: React.FormEvent) {
    e.preventDefault()
    if (!email || sending) return

    setSending(true)
    try {
      await apiV2.requestMagicLink(email, linkRedirect(target))
      setSent(true)
      cooldown.start()
      toast.success('Sign-in code and link sent! Check your email.')
    } catch (error) {
      if (error instanceof ApiV2Error && (error.code === 'signup_rate_limited' || error.code === 'disposable_email')) {
        // The free plan limits sign-ups per network and refuses disposable email domains: say so.
        toast.error(error.message)
      } else if (error instanceof ApiV2Error && error.status === 429) {
        toast.error('Too many requests. Try again in a few minutes.')
      } else {
        toast.error('Failed to send the sign-in email. Try again.')
      }
    } finally {
      setSending(false)
    }
  }

  async function handleResend() {
    setSending(true)
    try {
      await apiV2.requestMagicLink(email, linkRedirect(target))
      toast.success('Sign-in code and link resent!')
      cooldown.start()
    } catch {
      toast.error('Failed to resend. Try again in a moment.')
    } finally {
      setSending(false)
    }
  }

  if (freeUnavailable) return <FreePlanUnavailable full={freePlan.isFull} />

  if (sent) {
    return (
      <CheckEmailStep
        email={email}
        redirect={linkRedirect(target)}
        sending={sending}
        cooldownRemaining={cooldown.remaining}
        onResend={handleResend}
        onBack={() => { setSent(false); setEmail('') }}
      />
    )
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main id="main" className="flex flex-1 items-start justify-center px-4 py-20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" aria-label="Uptrack home" className="mx-auto mb-2 inline-flex"><Wordmark height={28} /></Link>
            <h1 data-slot="card-title" className="text-2xl leading-none font-semibold">
              {plan === 'free' ? 'Create your free account' : planName ? `Create your account for ${planName}` : 'Create your Uptrack account'}
            </h1>
            <CardDescription>
              {plan === 'free' ? "No password and no credit card. We'll email you a sign-in code." : "No password needed. We'll email you a sign-in code."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSendMagicLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                  required
                  autoFocus
                />
                <p
                  className={`text-xs ${plan === 'free' && looksLikeFreeMail(email) ? 'text-status-degraded' : 'text-muted-foreground'}`}
                  data-testid="signup-email-note"
                >
                  {plan === 'free'
                    ? `${FREE_DOMAIN_RULE} ${PAID_ANY_EMAIL}`
                    : `Any email address works on a paid plan. ${FREE_DOMAIN_RULE}`}
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={sending || !email}>
                {sending ? 'Sending...' : (
                  <>
                    Send sign-in code
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
            <OAuthDivider />
            <OAuthButtons />
          </CardContent>
          <CardFooter className="flex-col gap-2">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" search={{ redirect: redirectTo, plan, interval }} className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
            <p className="text-sm text-muted-foreground">
              {plan === 'free'
                ? `Next: add your first monitor. Free: ${freePlan.monitors} monitors, checked every ${freePlan.intervalSeconds} seconds from ${freePlan.location}.`
                : planName
                  ? 'Next: secure checkout. 30-day money-back guarantee.'
                  : freeOpen
                    ? `Next: add your first monitor on the free plan (${freePlan.monitors} monitors at ${freePlan.intervalSeconds} seconds), or pick a plan from $3/mo with a 30-day money-back guarantee.`
                    : 'Next: pick a plan. Pro starts at $3/mo. 30-day money-back guarantee.'}
            </p>
          </CardFooter>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}
